import React from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { ResponsiveContainer, BarChart as RechartsBarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import ChartHeader from './ChartHeader';
import ChartCardWrapper from './ChartCardWrapper';
import ChartLegend from './ChartLegend';
import { fontSizes } from '../../theme/theme';

interface ScoreCardData {
  [key: string]: string | number;
}

interface ScoreCardProps {
  title: string;
  description: string;
  data: ScoreCardData[];
  dataKeys: string[];
  xAxisKey: string;
  colors?: string[];
  height?: number;
  graphHeight?: number;
  stacked?: boolean;
  xAxisTitle?: string;
  yAxisTitle?: string;
  details?: any;
  gridExist?: boolean;
}

const ScoreCard: React.FC<ScoreCardProps> = ({
  title,
  description,
  data,
  dataKeys,
  xAxisKey,
  colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'],
  height = 300,
  graphHeight = 250,
  stacked = false,
  xAxisTitle,
  yAxisTitle,
  details,
  gridExist = false
}) => {

  const handleBarClick = (data: any) => {
    console.log('Bar clicked:', data);
  };

  const mainScore = details?.score ?? 0;
  const maxScore = details?.maxScore ?? 100;
  const scorePct = maxScore ? Math.round((Number(mainScore) / Number(maxScore)) * 100) : 0;
  const scoreColor = scorePct >= 70 ? '#16A34A' : scorePct >= 45 ? '#F59E0B' : '#DC2626';
  return (
    <ChartCardWrapper sx={{height}}>
      <ChartHeader title={title} description={description} details={details} />
      <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' }, alignItems: 'stretch' }}>
        <Box sx={{ flexBasis: { xs: '100%', md: '30%' }, display: 'flex', flexDirection: 'column', gap: 2, pr: { md: 3 }, alignItems: { xs: 'flex-start', md: 'stretch' } }}>
          <Box sx={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ position: 'relative', width: { xs: 92, sm: 140 }, height: { xs: 92, sm: 140 }, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress
                  variant="determinate"
                  value={100}
                  size={Math.max(92, 140)}
                  thickness={8}
                  sx={{
                    color: '#F3F4F6',
                    position: 'absolute'
                  }}
                />
                <CircularProgress
                  variant="determinate"
                  value={Math.min(Math.max(scorePct, 0), 100)}
                  size={Math.max(92, 140)}
                  thickness={8}
                  sx={{
                    position: 'absolute',
                    '& .MuiCircularProgress-circle': {
                      stroke: scoreColor,
                      strokeLinecap: 'round'
                    }
                  }}
                />
                <Box sx={{ position: 'absolute', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: scoreColor, lineHeight: 1 }}>{`${scorePct}%`}</Typography>
                  <Typography variant="caption" sx={{ color: '#6B7280' }}>{`${Number(mainScore).toLocaleString()} / ${maxScore}`}</Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2" sx={{ color: details?.label === 'Critical' ? '#f03817ff' : '#4d81d6ff', fontWeight: 800, fontSize: 30 }}>{details?.label ?? 'Score'}</Typography>
                <Typography variant="body2" sx={{ color: '#374151', fontSize: 16 }}>{details?.subLabel}</Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: 180 }}>
              {details?.subPoints?.map((point: any, idx: number) => (
                <Box key={point} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pt: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: colors[idx % colors.length], mr: 1 }} />
                    <Typography variant="body2" sx={{ color: '#374151', fontWeight: 600 }}>{point}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        <Box sx={{ flexBasis: { xs: '100%', md: '70%' }, minHeight: graphHeight }}>
          <ResponsiveContainer width="100%" height="100%">
            <RechartsBarChart
              data={data}
              margin={{ top: 20, right: 10, left: 10, bottom: 20 }}
              barCategoryGap="35%"
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
              <XAxis
                dataKey={xAxisKey}
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: fontSizes.graph.axis, fill: '#6B7280' }}
                label={xAxisTitle ? { value: xAxisTitle, position: 'insideBottom', offset: -10, style: { textAnchor: 'middle', fontSize: fontSizes.graph.axis, fill: '#374151' } } : undefined}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: fontSizes.graph.axis, fill: '#6B7280' }}
                label={yAxisTitle ? { value: yAxisTitle, angle: -90, position: 'insideLeft', offset: 20, style: { textAnchor: 'middle', fontSize: fontSizes.graph.axis, fill: '#374151' } } : undefined}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#F9FAFB',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  fontSize: `${fontSizes.graph.tooltip}px`
                }}
                cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }}
              />
              {dataKeys.map((key, index) => (
                <Bar
                  key={key}
                  dataKey={key}
                  fill={colors[index % colors.length]}
                  stackId={stacked ? "a" : undefined}
                  radius={stacked ? 0 : [4, 4, 0, 0]}
                  maxBarSize={35}
                  onClick={handleBarClick}
                  style={{ cursor: gridExist ? 'pointer' : 'default' }}
                />
              ))}
            </RechartsBarChart>
          </ResponsiveContainer>
        </Box>
      </Box>
      <ChartLegend 
        bottomDescription={details?.bottomDescription}
        subPoints={undefined}
        colors={colors}
      />
    </ChartCardWrapper>
  );
};

export default ScoreCard;
